import { useCallback, useEffect, useState } from 'react';
import type { INews, INewsCreate } from '@skystone/shared';
import { NEWS_KIND_META } from '@skystone/shared';
import { newsApi } from '@/services/news-api';
import {
  ArchivableListShell,
  type ArchivableColumn,
} from '@/components/admin/ArchivableListShell';
import { NEWS_KIND_ICON } from '@/lib/news-kind-icons';
import { getErrorMessage } from '@/lib/utils';
import { NewsEditor } from './NewsEditor';

const ADMIN_LIST_LIMIT = 100;

type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; item: INews };

function buildColumns(): ArchivableColumn<INews>[] {
  return [
    {
      header: 'Date',
      cell: (item) => <span className="text-sm tabular-nums">{item.eventDate}</span>,
      className: 'sm:max-w-[110px]',
    },
    {
      header: 'Kind',
      cell: (item) => {
        const Icon = NEWS_KIND_ICON[item.kind];
        return (
          <span className="inline-flex items-center gap-2 text-sm">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="uppercase tracking-wider text-muted-foreground">
              {NEWS_KIND_META[item.kind].verb}
            </span>
          </span>
        );
      },
      className: 'sm:max-w-[160px]',
    },
    {
      header: 'Title',
      cell: (item) => (
        <span className="block truncate text-sm font-medium" title={item.title}>
          {item.title}
        </span>
      ),
      className: 'sm:flex-[2]',
    },
    {
      header: 'Category',
      cell: (item) => (
        <span className="text-xs text-muted-foreground">{item.categoryTag ?? '—'}</span>
      ),
      className: 'sm:max-w-[140px]',
    },
  ];
}

export function NewsTab() {
  const [items, setItems] = useState<INews[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });

  const fetchItems = useCallback(async (includeArchived: boolean): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await newsApi.getAll({ includeArchived, limit: ADMIN_LIST_LIMIT });
      setItems(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems(showArchived);
  }, [fetchItems, showArchived]);

  const handleArchiveToggle = async (item: INews): Promise<void> => {
    try {
      await newsApi.setArchived(item.id, !item.archived);
      await fetchItems(showArchived);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleSave = async (data: INewsCreate): Promise<void> => {
    if (editor.mode === 'edit') {
      await newsApi.update(editor.item.id, data);
    } else {
      await newsApi.create(data);
    }
    setEditor({ mode: 'closed' });
    await fetchItems(showArchived);
  };

  const editorOpen = editor.mode !== 'closed';
  const editorInitialItem = editor.mode === 'edit' ? editor.item : null;

  return (
    <>
      <ArchivableListShell<INews>
        items={items}
        isLoading={isLoading}
        error={error}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
        onCreate={() => setEditor({ mode: 'create' })}
        onEdit={(item) => setEditor({ mode: 'edit', item })}
        onArchiveToggle={handleArchiveToggle}
        isArchived={(item) => item.archived}
        rowKey={(item) => item.id}
        columns={buildColumns()}
        createButtonLabel="Create news item"
        emptyMessage="No news items yet."
      />
      <NewsEditor
        open={editorOpen}
        initialItem={editorInitialItem}
        onClose={() => setEditor({ mode: 'closed' })}
        onSave={handleSave}
      />
    </>
  );
}
